import * as Logger from "./logger.js";
import * as Utils from './utils.js'
import { HSComponent } from "./component.js";
import { UserSession } from './session.js';
import styles from "./date-range-picker.css";
import template from "./date-range-picker.html";

export class HSDateRangePicker extends HSComponent {
    isOpen = false;
    startDate = null;
    endDate = null;
    currentMonth = new Date();
    isSelectingRange = true;   // false = single day picker, true = range picker
    hoverDate = null;

    constructor() {
        super();
    }

    onLoad() {
        const mode = this.getAttribute('mode') || 'range'; // 'single' or 'range'
        this.isSelectingRange = mode === 'range';

        this.loadTemplate(template);
        this.loadStyles(styles);

        this.render();
        this.attachEventListeners();
    }

    render() {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const weekdayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
        const days = this.getDaysInMonth(this.currentMonth);

        const dateText = this.shadowRoot.querySelector('.date-text');
        dateText.textContent = this.formatDateRange();

        const popup = this.shadowRoot.getElementById('popup');
        if (this.isOpen) {
            popup.classList.add('open');
        } else {
            popup.classList.remove('open');
            return;
        }

        // ensure the position of the popup does not go outside the viewport, and if it does, adjust it
        const input = this.shadowRoot.getElementById('input');
        const rect = input.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();
        if (rect.left + popupRect.width > window.innerWidth) {
            popup.style.left = 'auto';
            popup.style.right = '0px';
        }

        const monthYear = this.shadowRoot.querySelector('.month-year');
        monthYear.textContent = `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;

        const weekdays = this.shadowRoot.querySelector('.weekdays');
        weekdays.innerHTML = weekdayNames.map(day => `<div class="weekday">${day}</div>`).join('');

        const daysGrid = this.shadowRoot.querySelector('.days-grid');
        daysGrid.innerHTML = days.map(date => {
            const isCurrentMonth = this.isCurrentMonth(date);
            const isStart = this.startDate && this.isSameDay(date, this.startDate);
            const isEnd = this.endDate && this.isSameDay(date, this.endDate);
            const isSelected = isStart || isEnd;
            const isInRange = this.isSelectingRange && this.startDate && this.endDate && this.isInRange(date);
            const isWeekend = this.isWeekend(date);
            const isToday = this.isToday(date);

            let classes = 'day-cell';
            if (isCurrentMonth) { classes += ' current-month'; }
            else classes += ' other-month';
            if (isWeekend && isCurrentMonth) { classes += ' weekend'; }
            if (isToday) { classes += ' today'; }
            if (isSelected) { classes += ' selected'; }
            if (isInRange && !isSelected) { classes += ' in-range'; }
            if (isStart) { classes += ' range-start'; }
            if (isEnd) { classes += ' range-end'; }

            return `<div class="${classes}" data-date="${date.toISOString()}">
                        <div class="day-content">${date.getDate()}</div>
                    </div>`;
        }).join('');
    }

    formatDate(date) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear().toString().slice(-2);
        return `${day} ${month} ${year}`;
    }

    formatDateRange() {
        if (!this.startDate) return 'Select date';
        if (!this.endDate && this.isSelectingRange) return this.formatDate(this.startDate);
        if (!this.isSelectingRange) return this.formatDate(this.startDate);
        return `${this.formatDate(this.startDate)} – ${this.formatDate(this.endDate)}`;
    }

    isToday(date) {
        const today = new Date();
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear();
    }

    isSameDay(date1, date2) {
        return date1.getDate() === date2.getDate() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getFullYear() === date2.getFullYear();
    }

    isInRange(date) {
        if (!this.startDate || !this.endDate) { return false; }

        // Normalize dates to midnight for proper comparison
        const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const start = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).getTime();
        const end = new Date(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate()).getTime();

        return checkDate >= start && checkDate <= end;
    }

    isWeekend(date) {
        const day = date.getDay();
        return day === 0 || day === 6;
    }

    getDaysInMonth(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const days = [];

        // Add previous month's days
        const firstDayOfWeek = firstDay.getDay();
        const daysToAdd = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        for (let i = daysToAdd; i > 0; i--) {
            days.push(new Date(year, month, 1 - i));
        }

        // Add current month's days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        // Add next month's days to complete the grid
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push(new Date(year, month + 1, i));
        }

        return days;
    }

    isCurrentMonth(date) {
        return date.getMonth() === this.currentMonth.getMonth() &&
               date.getFullYear() === this.currentMonth.getFullYear();
    }

    setQuickDate(type) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (type == 'today') {
            this.startDate = new Date(today);
            this.endDate = new Date(today);
        } else if (type == 'yesterday') {
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            this.startDate = yesterday;
            this.endDate = yesterday;
        } else if (type == 'lastweek') {
            this.endDate = new Date(today);
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            this.startDate = weekAgo;
        } else if (type == 'lastmonth') {
            this.endDate = new Date(today);
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            this.startDate = monthAgo;
        } else if (type == 'lastquarter') {
            this.endDate = new Date(today);
            const quarterAgo = new Date(today);
            quarterAgo.setMonth(quarterAgo.getMonth() - 3);
            this.startDate = quarterAgo;
        }

        this.render();
        this.dispatchChangeEvent();
    }

    reset() {
        this.startDate = null;
        this.endDate = null;
        this.hoverDate = null;
        this.render();
        this.dispatchChangeEvent();
    }

    handleDateClick(date) {
        // Normalize date to midnight
        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (this.isSelectingRange) {
          if (!this.startDate || (this.startDate && this.endDate)) {
            this.startDate = normalizedDate;
            this.endDate = null;
          } else {
            if (normalizedDate < this.startDate) {
              this.endDate = this.startDate;
              this.startDate = normalizedDate;
            } else {
              this.endDate = normalizedDate;
            }
          }
        } else {
          this.startDate = normalizedDate;
          this.endDate = null;
        }

        this.render();
        this.dispatchChangeEvent();
    }

    dispatchChangeEvent() {
        this.emit({ type: 'hs-date-changed', 
            startDate: this.startDate,
            endDate: this.endDate,
            formattedValue: this.formatDateRange()
        });
    }

    attachEventListeners() {
        this.abort();   // drop all previous event handlers

        const input = this.shadowRoot.getElementById('input');
        const popup = this.shadowRoot.getElementById('popup');
        const prevBtn = this.shadowRoot.getElementById('prev-month');
        const nextBtn = this.shadowRoot.getElementById('next-month');
        const resetBtn = this.shadowRoot.getElementById('reset');

        this.onEvent('click', (obj, e) => {
            e.stopPropagation();
            this.isOpen = !this.isOpen;
            this.render();
            this.attachEventListeners();
        }, input);

        this.onEvent('click', (obj, e) => {
            e.stopPropagation();
        }, popup);

        this.onEvent('click', (obj, e) => {
            if (this.isOpen) {
                this.isOpen = false;
                this.render();
                this.attachEventListeners();
            }
        }, document);

        this.onEvent('click', (obj, e) => {
            this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1);
            this.render();
            this.attachEventListeners();
        }, prevBtn);

        this.onEvent('click', (obj, e) => {
            this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1);
            this.render();
            this.attachEventListeners();
        }, nextBtn);

        this.onEvent('click', (obj, e) => {
            this.reset();
            this.attachEventListeners();
        }, resetBtn);

        // Quick date options
        this.shadowRoot.querySelectorAll('.quick-option').forEach(option => {
            this.onEvent('click', (obj, e) => {
                const action = option.getAttribute('data-action');
                if (action) this.setQuickDate(action);
                this.attachEventListeners();
            }, option);
        });

        // Day cells
        this.shadowRoot.querySelectorAll('.day-cell').forEach(cell => {
            this.onEvent('click', (obj, e) => {
                const dateStr = cell.getAttribute('data-date');
                if (dateStr) {
                    this.handleDateClick(new Date(dateStr));
                    this.attachEventListeners();
                }
            }, cell);
        });
    }

    getValue() {
        return {
            startDate: this.startDate,
            endDate: this.endDate,
            formattedValue: this.formatDateRange()
        };
    }

    setValue(startDate, endDate = null) {
        this.startDate = startDate;
        this.endDate = endDate;
        if (startDate) {
            this.currentMonth = new Date(startDate);
        }
        this.render();
        this.attachEventListeners();
    }
}
customElements.define("hs-date-range-picker", HSDateRangePicker);
